import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { formatDate } from '@angular/common';
import { EMPTY } from 'rxjs';
import { expand, finalize, map, reduce, switchMap, take, tap } from 'rxjs/operators';
import {
  productTypeLabel,
  subscriptionEventBadgeClass,
  subscriptionEventLabel,
  SubscriptionEvent,
  SubscriptionInvoiceReport,
} from '../../model/v3/subscriptions';
import { ManagerUtils } from '../../services/utils/v3/manager-utils';
import { SubscriptionsV3RestService } from '../../services/rest/v3/subscriptions-rest.service';
import { ExportExcelService } from '../../services/export-excel.service';
import { Page, Pageable } from '../../model/rest/base';

@Component({
  selector: 'app-manager-subscriptions-page',
  templateUrl: './manager-subscriptions-page.component.html',
  standalone: false,
})
export class ManagerSubscriptionsPageComponent implements OnInit {
  schoolId: number;
  month: string;
  report: SubscriptionInvoiceReport | null = null;
  auditPage: Page<SubscriptionEvent> | null = null;
  auditPageable = Pageable.of(0, 25, null);
  loading = false;

  constructor(
    private route: ActivatedRoute,
    private subscriptionsRest: SubscriptionsV3RestService,
    private exportExcel: ExportExcelService,
  ) {}

  ngOnInit(): void {
    this.month = new Date().toISOString().substring(0, 7);
    this.route.parent.paramMap
      .pipe(
        take(1),
        tap((params) => (this.schoolId = +params.get('schoolId'))),
      )
      .subscribe(() => this.load());
  }

  load() {
    if (!this.month || !this.schoolId) {
      return;
    }
    this.loading = true;
    this.auditPageable = Pageable.of(0, 25, null);
    this.subscriptionsRest
      .getSchoolInvoiceReport(this.schoolId, this.month)
      .pipe(
        tap((reports) => (this.report = reports[0] ?? null)),
        switchMap(() =>
          this.subscriptionsRest.getSchoolAuditReport(
            this.schoolId, this.month, this.auditPageable,
          ),
        ),
        tap((page) => (this.auditPage = page)),
        finalize(() => (this.loading = false)),
      )
      .subscribe();
  }

  switchAuditPage(pageable: Pageable) {
    this.auditPageable = pageable;
    this.subscriptionsRest
      .getSchoolAuditReport(this.schoolId, this.month, this.auditPageable)
      .subscribe((page) => (this.auditPage = page));
  }

  personName = ManagerUtils.getName;
  productLabel = productTypeLabel;

  eventBadgeClass(event: SubscriptionEvent): string {
    return subscriptionEventBadgeClass(event.eventType);
  }

  eventLabel(event: SubscriptionEvent): string {
    return subscriptionEventLabel(event);
  }

  exportAudit() {
    // export always covers the full month, not just the visible page; large
    // schools exceed a single response, so the export pages through everything
    const size = 1000;
    this.subscriptionsRest
      .getSchoolAuditReport(this.schoolId, this.month, Pageable.of(0, size, null))
      .pipe(
        expand((page) =>
          page.last
            ? EMPTY
            : this.subscriptionsRest.getSchoolAuditReport(
                this.schoolId, this.month, Pageable.of(page.number + 1, size, null),
              ),
        ),
        map((page) => page.content),
        reduce(
          (all, rows) => all.concat(rows),
          [] as SubscriptionEvent[],
        ),
      )
      .subscribe((events) => this.exportAuditRows(events));
  }

  private exportAuditRows(events: SubscriptionEvent[]) {
    if (!events.length) {
      return;
    }
    const rows = events.map((event) => ({
      person: ManagerUtils.getName(event.person),
      product: productTypeLabel(event.productType),
      event: subscriptionEventLabel(event),
      source: event.eventSource,
      billingMonth: event.billingMonth,
      effectiveDate: formatDate(event.effectiveDate, 'yyyy-MM-dd', 'en'),
      status: event.subscriptionStatus,
      notes: event.notes ?? '',
    }));
    this.exportExcel.exportToExcel(
      rows,
      `subscriptions-audit-${this.schoolId}-${this.month}`,
      'default',
    );
  }

}
